
import { computed, defineComponent, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// module components
import CalculatorForm from '@/modules/CalculatorModule/components/CalculatorForm/index.vue'
import CalculatorFormFtl from '@/modules/CalculatorModule/components/CalculatorFormFtl/index.vue'
import CalculatorResult from '@/modules/CalculatorModule/components/CalculatorResult/index.vue'
import CalculatorTabs from '@/modules/CalculatorModule/components/CalculatorTabs/index.vue'

// types
import {
    CalculatorComponentPayload,
    BillingCalculateCostParams,
    CalculatorPhysicalWeights,
    CalculatorVolumeWeights,
    CalculatorAdditionalService,
    BillingCalculateCostResponseAdditionalServices,
    CalculatorFtlComponentPayload,
    BillingCalculateFtlCostParams,
} from '@/core/types/billing.types'

// realisations
import { billing } from '@/core/realisations/billing.realisation'

// composable
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        CalculatorForm,
        CalculatorResult,
        CalculatorTabs,
        CalculatorFormFtl,
    },
    setup() {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_form_submited: false,
            is_loaded: false,
            currentTab: 'ltl',
            //
            company_id: null as number | null,
            weight: null as number | null,
            tariff_id: null as number | null,
            //
            from_city_id: null as number | null,
            from_city_name: null as string | null,
            where_city_id: null as number | null,
            where_city_name: null as string | null,
            is_cities_equals: false,
            //
            is_has_pickup: false,
            is_has_self_delivery: false,
            //
            physicalWeights: [] as CalculatorPhysicalWeights,
            volumeWeights: [] as CalculatorVolumeWeights,
            //
            declaredPrice: null as number | null,
            productName: null as string | null,
            //
            additionalServicesDelivery: [] as CalculatorAdditionalService[],
            //
            econom: {
                additionalServiceDelivery:
                    null as BillingCalculateCostResponseAdditionalServices | null,
                additionalServiceTake:
                    null as BillingCalculateCostResponseAdditionalServices | null,
                result: 0,
                insuranceCost: 0,
                packagingCost: 0,
                unloadCost: 0,
                days: 0,
                usedWeightInKg: 0,
                weightTypeToCalculate: null as string | null,
                deliveryCharge: 0,
            },
            express: {
                additionalServiceDelivery:
                    null as BillingCalculateCostResponseAdditionalServices | null,
                additionalServiceTake:
                    null as BillingCalculateCostResponseAdditionalServices | null,
                result: 0,
                insuranceCost: 0,
                packagingCost: 0,
                unloadCost: 0,
                days: 0,
                usedWeightInKg: 0,
                weightTypeToCalculate: null as string | null,
                deliveryCharge: 0,
            },
        })

        const state_ftl = reactive({
            is_loading: false,
            is_form_submited: false,
            is_loaded: false,
            currentTab: 'ltl',
            //
            company_id: null as number | null,
            //
            from_city_id: null as number | null,
            from_city_name: null as string | null,
            where_city_id: null as number | null,
            where_city_name: null as string | null,
            //
            duration: null as number | null,
            volume: null as number | null,
            //
            additionalServicesDelivery: [] as CalculatorAdditionalService[],
            //
            econom: {
                additionalServiceDelivery:
                    null as BillingCalculateCostResponseAdditionalServices | null,
                additionalServiceTake:
                    null as BillingCalculateCostResponseAdditionalServices | null,
                finalCost: 0,
                deliveryCost: 0,
                downtimeCost: 0,
                vatCoefficient: 0,
            },
        })

        const resetState = () => {
            state.is_form_submited = false
            state.is_loaded = false

            state.tariff_id = null
            state.company_id = null
            state.weight = null

            state.from_city_name = null
            state.from_city_id = null
            state.where_city_name = null
            state.where_city_id = null
            state.is_cities_equals = false

            state.is_has_pickup = false
            state.is_has_self_delivery = false

            state.physicalWeights = []
            state.volumeWeights = []

            state.declaredPrice = null
            state.productName = null
            state.additionalServicesDelivery = []

            state.econom.additionalServiceDelivery = null
            state.econom.additionalServiceTake = null
            state.econom.result = 0
            state.econom.insuranceCost = 0
            state.econom.deliveryCharge = 0
            state.econom.packagingCost = 0
            state.econom.unloadCost = 0
            state.econom.days = 0
            state.econom.usedWeightInKg = 0
            state.econom.weightTypeToCalculate = null

            state.express.additionalServiceDelivery = null
            state.express.additionalServiceTake = null
            state.express.result = 0
            state.express.insuranceCost = 0
            state.express.deliveryCharge = 0
            state.express.packagingCost = 0
            state.express.unloadCost = 0
            state.express.days = 0
            state.express.usedWeightInKg = 0
            state.express.weightTypeToCalculate = null
        }

        const resetStateFtl = () => {
            state_ftl.is_form_submited = false
            state_ftl.is_loaded = false

            state_ftl.company_id = null

            state_ftl.from_city_name = null
            state_ftl.from_city_id = null
            state_ftl.where_city_name = null
            state_ftl.where_city_id = null

            state_ftl.duration = null
            state_ftl.volume = null
            state_ftl.additionalServicesDelivery = []

            state_ftl.econom.additionalServiceDelivery = null
            state_ftl.econom.additionalServiceTake = null
            state_ftl.econom.finalCost = 0
            state_ftl.econom.deliveryCost = 0
            state_ftl.econom.downtimeCost = 0
            state_ftl.econom.vatCoefficient = 0
        }

        const onFormSubmit = async (payload: CalculatorComponentPayload) => {
            resetState()
            state.is_form_submited = true

            state.tariff_id = payload.tariff_id
            state.company_id = payload.company_id

            if (payload.weight) {
                state.weight = payload.weight
            }

            state.from_city_name = payload.from_city.name
            state.from_city_id = payload.from_city.id
            state.where_city_name = payload.where_city.name
            state.where_city_id = payload.where_city.id

            state.is_cities_equals = payload.from_city.id === payload.where_city.id

            state.volumeWeights = [...payload.volumeWeights]
            state.physicalWeights = [...payload.physicalWeights]

            state.is_has_pickup = payload.is_has_pickup
            state.is_has_self_delivery = payload.is_has_self_delviery

            if (payload.declaredPrice) {
                state.declaredPrice = payload.declaredPrice
            }

            if (payload.productName) {
                state.productName = payload.productName
            }

            state.additionalServicesDelivery = payload.additionalServicesDelivery

            try {
                state.is_loading = true

                const econom_params: BillingCalculateCostParams = {
                    isFromMainCity: payload.direction.isFromMainCity,
                    isNearbyCities: payload.direction.isNearbyCities,
                    zoneId: payload.direction.zoneId,
                    shipmentTypeId: 1,
                    hasPickup: Number(payload.is_has_pickup),
                    hasSelfDelivery: Number(payload.is_has_self_delviery),
                    isOrderPrice: Number(payload.isOrderPrice),
                    volumeWeights: state.volumeWeights,
                    physicalWeights: state.physicalWeights,
                    additionalServicesDelivery: state.additionalServicesDelivery,
                    fromCityId: state.from_city_id as number,
                    whereCityId: state.where_city_id as number,
                }

                const express_params: BillingCalculateCostParams = {
                    isFromMainCity: payload.direction.isFromMainCity,
                    isNearbyCities: payload.direction.isNearbyCities,
                    zoneId: payload.direction.zoneId,
                    shipmentTypeId: 2,
                    hasPickup: Number(payload.is_has_pickup),
                    hasSelfDelivery: Number(payload.is_has_self_delviery),
                    isOrderPrice: Number(payload.isOrderPrice),
                    volumeWeights: state.volumeWeights,
                    physicalWeights: state.physicalWeights,
                    additionalServicesDelivery: state.additionalServicesDelivery,
                    fromCityId: state.from_city_id as number,
                    whereCityId: state.where_city_id as number,
                }

                if (payload.company_id) {
                    econom_params.companyId = payload.company_id
                    express_params.companyId = payload.company_id
                }

                if (payload.declaredPrice) {
                    econom_params.declaredPrice = payload.declaredPrice
                    express_params.declaredPrice = payload.declaredPrice
                }

                if (payload.productName) {
                    econom_params.productName = payload.productName
                    express_params.productName = payload.productName
                }

                if (payload.weight) {
                    econom_params.weight = payload.weight
                    express_params.weight = payload.weight
                }

                if (payload.tariff_id === 7) {
                    const econom_response = await billing.calculateCostV3(econom_params)
                    state.econom.additionalServiceDelivery =
                        econom_response.result.additionalServiceDelivery
                    state.econom.additionalServiceTake =
                        econom_response.result.additionalServiceTake
                    state.econom.result = econom_response.result.result
                    state.econom.insuranceCost = econom_response.result.insuranceCost
                    state.econom.deliveryCharge = econom_response.result.deliveryCharge
                    state.econom.packagingCost = econom_response.result.packagingCost
                    state.econom.unloadCost = econom_response.result.unloadCost
                    state.econom.usedWeightInKg = econom_response.result.usedWeightInKg
                    state.econom.weightTypeToCalculate =
                        econom_response.result.weightTypeToCalculate
                    state.econom.days = payload.direction.standart_delivery_time
                } else {
                    const econom_response = await billing.calculateCost(econom_params)
                    state.econom.additionalServiceDelivery =
                        econom_response.result.additionalServiceDelivery
                    state.econom.additionalServiceTake =
                        econom_response.result.additionalServiceTake
                    state.econom.result = econom_response.result.result
                    state.econom.insuranceCost = econom_response.result.insuranceCost
                    state.econom.deliveryCharge = econom_response.result.deliveryCharge
                    state.econom.packagingCost = econom_response.result.packagingCost
                    state.econom.unloadCost = econom_response.result.unloadCost
                    state.econom.usedWeightInKg = econom_response.result.usedWeightInKg
                    state.econom.weightTypeToCalculate =
                        econom_response.result.weightTypeToCalculate
                    state.econom.days = payload.direction.standart_delivery_time
                }

                if (!state.is_cities_equals) {
                    if (payload.tariff_id === 7) {
                        const express_response = await billing.calculateCostV3(express_params)
                        state.express.additionalServiceDelivery =
                            express_response.result.additionalServiceDelivery
                        state.express.additionalServiceTake =
                            express_response.result.additionalServiceTake
                        state.express.result = express_response.result.result
                        state.express.insuranceCost = express_response.result.insuranceCost
                        state.express.deliveryCharge = express_response.result.deliveryCharge
                        state.express.packagingCost = express_response.result.packagingCost
                        state.express.unloadCost = express_response.result.unloadCost
                        state.express.usedWeightInKg = express_response.result.usedWeightInKg
                        state.express.weightTypeToCalculate =
                            express_response.result.weightTypeToCalculate
                        state.express.days = payload.direction.express_delivery_time
                    } else {
                        const express_response = await billing.calculateCost(express_params)
                        state.express.additionalServiceDelivery =
                            express_response.result.additionalServiceDelivery
                        state.express.additionalServiceTake =
                            express_response.result.additionalServiceTake
                        state.express.result = express_response.result.result
                        state.express.insuranceCost = express_response.result.insuranceCost
                        state.express.deliveryCharge = express_response.result.deliveryCharge
                        state.express.packagingCost = express_response.result.packagingCost
                        state.express.unloadCost = express_response.result.unloadCost
                        state.express.usedWeightInKg = express_response.result.usedWeightInKg
                        state.express.weightTypeToCalculate =
                            express_response.result.weightTypeToCalculate
                        state.express.days = payload.direction.express_delivery_time
                    }
                }

                state.is_loaded = true
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При рассчете стоимости произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const onFormFtlSubmit = async (payload: CalculatorFtlComponentPayload) => {
            resetStateFtl()
            state_ftl.is_form_submited = true

            state_ftl.company_id = payload.companyId

            state_ftl.from_city_name = payload.from_city.name
            state_ftl.from_city_id = payload.from_city.id
            state_ftl.where_city_name = payload.where_city.name
            state_ftl.where_city_id = payload.where_city.id

            state_ftl.volume = payload.volume
            state_ftl.duration = payload.duration

            state_ftl.additionalServicesDelivery = payload.additionalServicesDelivery

            try {
                state_ftl.is_loading = true

                const params: BillingCalculateFtlCostParams = {
                    zoneId: payload.direction.zoneId,
                    additionalServicesDelivery: state_ftl.additionalServicesDelivery,
                    volume: payload.volume,
                    duration: payload.duration,
                }

                if (payload.companyId) {
                    params.companyId = payload.companyId
                }

                const response = await billing.calculateCostFtl(params)

                state_ftl.econom.additionalServiceDelivery =
                    response.result.additionalServiceDelivery
                state_ftl.econom.additionalServiceTake = response.result.additionalServiceTake
                state_ftl.econom.finalCost = response.result.finalCost
                state_ftl.econom.deliveryCost = response.result.deliveryCost
                state_ftl.econom.downtimeCost = response.result.downtimeCost
                state_ftl.econom.vatCoefficient = response.result.vatCoefficient

                state_ftl.is_loaded = true
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При рассчете стоимости произошла ошибка',
                })
            } finally {
                state_ftl.is_loading = false
            }
        }

        const isLtl = computed(() => state.currentTab === 'ltl')
        const isFtl = computed(() => state.currentTab === 'ftl')

        return {
            state,
            state_ftl,
            onFormSubmit,
            onFormFtlSubmit,
            isLtl,
            isFtl,
        }
    },
})
